import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Card } from '../components/ui/card';
import { Users, Package, FileText, DollarSign, Handshake, BarChart3, Settings as SettingsIcon, ShoppingCart } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { canAccessPage } from '../utils/permissions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Dashboard = () => {
  const { getAuthHeader, user } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/dashboard/stats`, { headers: getAuthHeader() });
        setStats(response.data);
      } catch (error) {
        console.error('Error fetching stats:', error);
      }
    };
    fetchStats();
  }, []);

  const balance = ((stats?.total_debit || 0) - (stats?.total_credit || 0)).toFixed(2);
  const isAr = language === 'ar';

  const allCards = [
    {
      key: 'finance', path: '/finance', icon: DollarSign, color: 'text-blue-500',
      subtitle: `${isAr ? 'الرصيد:' : 'Balance:'} ${balance} ${t('currency')}`,
    },
    {
      key: 'invoicing', path: '/invoicing', icon: FileText, color: 'text-purple-500',
      label: isAr ? 'فواتير وإشعارات تسليم' : 'Invoicing & Delivery Notes',
    },
    {
      key: 'clients', path: '/clients-suppliers?tab=clients', checkPath: '/clients-suppliers', icon: Users, color: 'text-blue-400',
      subtitle: `${stats?.clients || 0} ${t('clients')}`,
    },
    {
      key: 'suppliers', path: '/clients-suppliers?tab=suppliers', checkPath: '/clients-suppliers', icon: Package, color: 'text-green-500',
      subtitle: `${stats?.suppliers || 0} ${t('suppliers')}`,
    },
    {
      key: 'orders', path: '/inventory', icon: ShoppingCart, color: 'text-orange-500',
      subtitle: `${isAr ? 'إنتاج' : 'Prod.'}: ${stats?.in_production_orders || 0}  ·  ${isAr ? 'معلق' : 'Pending'}: ${stats?.pending_orders || 0}`,
    },
    { key: 'hrm',        path: '/hrm',         icon: Users,        color: 'text-pink-500' },
    { key: 'outsourcing',path: '/outsourcing',  icon: Handshake,    color: 'text-yellow-600' },
    { key: 'reports',    path: '/reports',      icon: BarChart3,    color: 'text-teal-500' },
    { key: 'settings',   path: '/settings',     icon: SettingsIcon, color: 'text-slate-500' },
  ];

  const cardRows = [
    ['finance', 'invoicing', 'clients', 'suppliers'],
    ['orders', 'hrm', 'outsourcing'],
    ['reports', 'settings'],
  ].map(keys =>
    keys.map(k => allCards.find(c => c.key === k)).filter(c => c && canAccessPage(user?.role, c.checkPath || c.path))
  ).filter(row => row.length > 0);

  return (
    <div className="p-4 md:p-8 space-y-6 md:space-y-8" dir={isAr ? 'rtl' : 'ltr'}>
      <div>
        <h1 className="text-2xl md:text-4xl font-bold tracking-tight" lang={isAr ? 'ar' : 'en'} data-testid="dashboard-title">
          {t('dashboard')}
        </h1>
        <p className="text-muted-foreground mt-2" lang={isAr ? 'ar' : 'en'}>{t('overviewSubtitle')}</p>
      </div>

      <div className="space-y-4">
        {cardRows.map((row, rowIdx) => (
          <div key={rowIdx} className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {row.map(item => {
              const Icon = item.icon;
              return (
                <Card
                  key={item.key}
                  className="p-4 border border-border hover:border-slate-300 transition-colors cursor-pointer flex flex-col items-center justify-center gap-2 text-center h-28"
                  onClick={() => navigate(item.path)}
                >
                  <Icon className={`w-8 h-8 ${item.color}`} />
                  <span
                    className={`font-semibold leading-tight ${isAr ? 'text-base' : 'text-sm'}`}
                    lang={isAr ? 'ar' : 'en'}
                  >
                    {item.label || t(item.key)}
                  </span>
                  {item.subtitle && (
                    <span
                      className={`text-muted-foreground font-mono-numeric ${isAr ? 'text-sm' : 'text-xs'}`}
                      dir="ltr"
                    >
                      {item.subtitle}
                    </span>
                  )}
                </Card>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};
